import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import MinimalLayout from '../components/layout/MinimalLayout';
import { 
  EnvelopeIcon, 
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

interface EmailVerificationProps {
  email?: string;
  userType?: 'teacher' | 'student';
}

const EmailVerificationPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  
  // Get email and user type from navigation state
  const { email, userType = 'teacher' } = location.state as EmailVerificationProps || {};
  
  console.log('Email verification page loaded for:', email, 'Type:', userType);

  // If no email provided, redirect back
  useEffect(() => {
    if (!email) {
      console.warn('No email provided for verification, redirecting');
      navigate('/become-a-teacher');
    }
  }, [email, navigate]);

  const handleResendEmail = async () => {
    if (!email) return;
    
    setIsResending(true);
    console.log('Resending verification email to:', email);
    
    try {
      // TODO: Call backend API to resend verification email
      // await resendVerificationEmail(email);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      setResendSuccess(true);
      setTimeout(() => setResendSuccess(false), 3000);
      
    } catch (error) {
      console.error('Error resending verification email:', error);
      alert('Failed to resend email. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  const handleBackToSignup = () => {
    console.log('Returning to signup page');
    navigate('/become-a-teacher');
  };

  const handleContinueToProfile = () => {
    console.log('User manually continuing to profile setup');
    // For development/testing - in production this would only be accessible after email verification
    navigate('/teacher-photo-setup', { 
      state: { 
        email,
        userType,
        skipEmailVerification: true // For testing purposes
      } 
    });
  };

  if (!email) {
    return null; // Will redirect via useEffect
  }

  return (
    <MinimalLayout noPadding>
      <div className="min-h-screen bg-slate-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="bg-slate-800 p-8 rounded-xl shadow-2xl">
            
            {/* Header */}
            <div className="text-center mb-8">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-yellow-500/20 mb-4">
                <EnvelopeIcon className="h-8 w-8 text-yellow-400" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">
                Check Your Email
              </h1>
              <p className="text-slate-400 text-sm">
                We've sent a verification link to
              </p>
              <p className="text-yellow-400 font-semibold mt-1">
                {email}
              </p>
            </div>

            {/* Instructions */}
            <div className="space-y-4 mb-8">
              <div className="bg-slate-700/50 p-4 rounded-lg border border-slate-600">
                <h3 className="text-slate-200 font-medium mb-2">Next Steps:</h3>
                <ol className="text-sm text-slate-400 space-y-2">
                  <li className="flex items-start space-x-2">
                    <span className="text-yellow-400 font-bold">1.</span>
                    <span>Check your email inbox (and spam folder)</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-yellow-400 font-bold">2.</span>
                    <span>Click the verification link in the email</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-yellow-400 font-bold">3.</span>
                    <span>Complete your profile setup</span>
                  </li>
                </ol>
              </div>
              
              <div className="bg-blue-500/10 border border-blue-500/30 p-4 rounded-lg">
                <div className="flex items-start space-x-2">
                  <ExclamationTriangleIcon className="h-5 w-5 text-blue-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-blue-300 text-sm font-medium">Don't see the email?</p>
                    <p className="text-blue-200 text-xs mt-1">
                      Check your spam folder or try resending the verification email.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Success Message */}
            {resendSuccess && (
              <div className="mb-6 bg-green-500/20 border border-green-500/50 rounded-lg p-4">
                <div className="flex items-center space-x-2">
                  <CheckCircleIcon className="h-5 w-5 text-green-400" />
                  <p className="text-green-300 text-sm font-medium">
                    Verification email sent successfully!
                  </p>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                onClick={handleResendEmail}
                disabled={isResending}
                className="w-full bg-yellow-500 hover:bg-yellow-600 disabled:bg-yellow-500/50 text-slate-900 font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center space-x-2"
              >
                {isResending ? (
                  <>
                    <ArrowPathIcon className="h-5 w-5 animate-spin" />
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <EnvelopeIcon className="h-5 w-5" />
                    <span>Resend Verification Email</span>
                  </>
                )}
              </button>
              
              {/* Development/Testing Button */}
              {process.env.NODE_ENV === 'development' && (
                <button
                  onClick={handleContinueToProfile}
                  className="w-full border border-slate-600 text-slate-300 hover:text-slate-100 hover:border-slate-500 font-semibold py-3 px-6 rounded-lg transition-colors"
                >
                  Continue to Profile (Dev Mode)
                </button>
              )}
              
              <button
                onClick={handleBackToSignup}
                className="w-full text-slate-400 hover:text-slate-200 font-medium py-2 text-sm transition-colors"
              >
                ← Back to Sign Up
              </button>
            </div>

            {/* Footer */}
            <div className="mt-8 pt-6 border-t border-slate-600">
              <p className="text-xs text-slate-500 text-center">
                Having trouble? <a href="/contact" className="text-yellow-400 hover:text-yellow-300">Contact our support team</a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </MinimalLayout>
  );
};

export default EmailVerificationPage; 